import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import {
  getLiveSessionAnalytics,
  LiveSessionAnalytics,
} from '../services/liveAccessApi';

const formatDate = (value?: string | null) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = (seconds: number | null | undefined): string => {
  if (!seconds) return 'N/A';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const StatCell: React.FC<{ label: string; value: React.ReactNode; tone?: string }> = ({
  label,
  value,
  tone = 'text-gray-900',
}) => (
  <div className='rounded-lg border border-gray-200 bg-gray-50 px-4 py-3'>
    <p className='text-xs uppercase tracking-wide text-gray-400'>{label}</p>
    <p className={`mt-1 text-lg font-semibold ${tone}`}>{value}</p>
  </div>
);

const LivestreamAnalyticsDetail: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState<LiveSessionAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const data = await getLiveSessionAnalytics(sessionId);
      setAnalytics(data);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  const s = analytics?.session;
  const e = analytics?.engagement;
  const n = analytics?.notifications;
  const p = analytics?.polls;
  const m = analytics?.moderation;
  const host = analytics?.host;

  return (
    <div className='min-h-screen bg-gray-50'>
      <Navbar />
      <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <button
          onClick={() => navigate('/live-access/analytics')}
          className='mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors'
        >
          <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
          </svg>
          Back to Livestream Analytics
        </button>

        {loading ? (
          <div className='flex items-center justify-center py-24 text-sm text-gray-500'>
            Loading analytics...
          </div>
        ) : !analytics ? (
          <div className='flex items-center justify-center py-24 text-sm text-gray-500'>
            Session not found.
          </div>
        ) : (
          <div className='space-y-6'>
            {/* Session Overview */}
            <div className='rounded-xl border border-gray-200 bg-white shadow-sm'>
              <div className='border-b border-gray-200 px-5 py-4 flex items-start gap-4'>
                {s?.coverUrl && (
                  <img
                    src={s.coverUrl}
                    alt='cover'
                    className='h-20 w-20 rounded-xl object-cover flex-shrink-0 border border-gray-200'
                  />
                )}
                <div className='min-w-0 flex-1'>
                  <h1 className='text-2xl font-bold text-gray-900'>{s?.title || 'Untitled live'}</h1>
                  <div className='mt-2 flex flex-wrap items-center gap-2'>
                    {s?.lifecycleStatus && (
                      <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
                        s.lifecycleStatus === 'LIVE'
                          ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                          : s.lifecycleStatus === 'ENDED'
                          ? 'bg-slate-100 text-slate-700 border-slate-200'
                          : 'bg-amber-100 text-amber-700 border-amber-200'
                      }`}>{s.lifecycleStatus}</span>
                    )}
                    {s?.publishStatus && (
                      <span className='inline-flex rounded-full border border-blue-200 bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700'>
                        {s.publishStatus}
                      </span>
                    )}
                    {s?.category && (
                      <span className='inline-flex rounded-full border border-gray-200 bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600'>
                        {s.category}
                      </span>
                    )}
                    {s?.contentRating && (
                      <span className='inline-flex rounded-full border border-purple-200 bg-purple-50 px-2.5 py-0.5 text-xs font-medium text-purple-700'>
                        {s.contentRating}
                      </span>
                    )}
                    {s?.hadPause && (
                      <span className='inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700'>
                        Host paused
                      </span>
                    )}
                  </div>
                  {host && (
                    <p className='mt-2 text-sm text-gray-500'>
                      Host:{' '}
                      <span className='font-medium text-gray-800'>
                        {host.Name || (host.UserName ? `@${host.UserName}` : host.UserID)}
                      </span>
                      <span className='ml-2 font-mono text-xs text-gray-400'>{host.UserID}</span>
                    </p>
                  )}
                  <p className='mt-1 font-mono text-xs text-gray-400'>{analytics.sessionId}</p>
                </div>
              </div>
              <div className='grid grid-cols-2 gap-3 p-5 sm:grid-cols-4'>
                <StatCell label='Started' value={formatDate(s?.startedAt)} />
                <StatCell label='Ended' value={formatDate(s?.endedAt)} />
                <StatCell label='Duration' value={formatDuration(s?.durationSeconds)} />
                <StatCell
                  label='Recording'
                  value={s?.recordingStatus || 'N/A'}
                  tone={s?.recordingStatus === 'COMPLETED' ? 'text-emerald-600' : 'text-gray-900'}
                />
                <StatCell label='Recording Length' value={formatDuration(s?.recordingDurationSeconds)} />
                <StatCell label='Chat' value={s?.policy?.chatEnabled ? 'Enabled' : 'Disabled'} />
                <StatCell
                  label='Slow Mode'
                  value={s?.policy?.slowModeSeconds ? `${s.policy.slowModeSeconds}s` : 'Off'}
                />
                <StatCell
                  label='Followers Only Chat'
                  value={s?.policy?.followersOnlyChat ? 'Yes' : 'No'}
                />
              </div>
            </div>

            {/* Recording Player */}
            {s?.recordingUrl ? (
              <div className='rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden'>
                <div className='border-b border-gray-200 px-5 py-4 flex items-center justify-between'>
                  <div>
                    <h2 className='text-base font-semibold text-gray-900'>Recording</h2>
                    {s.recordingDurationSeconds && (
                      <p className='text-sm text-gray-500'>Length: {formatDuration(s.recordingDurationSeconds)}</p>
                    )}
                  </div>
                  <a
                    href={s.recordingUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors'
                  >
                    <svg className='h-3.5 w-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14' />
                    </svg>
                    Open
                  </a>
                </div>
                <video
                  src={s.recordingUrl}
                  controls
                  controlsList='nodownload'
                  className='w-full max-h-[520px] bg-black'
                  preload='metadata'
                />
              </div>
            ) : s?.recordingStatus && s.recordingStatus !== 'DELETED' ? (
              <div className='rounded-xl border border-gray-200 bg-white shadow-sm'>
                <div className='border-b border-gray-200 px-5 py-4'>
                  <h2 className='text-base font-semibold text-gray-900'>Recording</h2>
                </div>
                <div className='flex items-center gap-3 px-5 py-6 text-sm text-gray-500'>
                  <svg className='h-5 w-5 flex-shrink-0 text-gray-300' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M15 10l4.553-2.069A1 1 0 0121 8.82v6.36a1 1 0 01-1.447.89L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z' />
                  </svg>
                  Recording status: <span className='font-medium text-gray-700'>{s.recordingStatus}</span>
                </div>
              </div>
            ) : null}

            {/* Engagement */}
            <div className='rounded-xl border border-gray-200 bg-white shadow-sm'>
              <div className='border-b border-gray-200 px-5 py-4'>
                <h2 className='text-base font-semibold text-gray-900'>Engagement</h2>
              </div>
              <div className='p-5 space-y-5'>
                <div className='grid grid-cols-2 gap-4 sm:grid-cols-4'>
                  <div className='rounded-xl border border-gray-200 bg-white p-4 shadow-sm text-center'>
                    <p className='text-sm text-gray-500'>Unique Viewers</p>
                    <p className='mt-1 text-3xl font-bold text-gray-900'>{e?.totalUniqueViewers ?? 0}</p>
                  </div>
                  <div className='rounded-xl border border-gray-200 bg-white p-4 shadow-sm text-center'>
                    <p className='text-sm text-gray-500'>Total Likes</p>
                    <p className='mt-1 text-3xl font-bold text-rose-600'>{e?.totalLikes ?? 0}</p>
                  </div>
                  <div className='rounded-xl border border-gray-200 bg-white p-4 shadow-sm text-center'>
                    <p className='text-sm text-gray-500'>Top Senders</p>
                    <p className='mt-1 text-3xl font-bold text-amber-600'>{e?.topSenders?.length ?? 0}</p>
                  </div>
                  <div className='rounded-xl border border-gray-200 bg-white p-4 shadow-sm text-center'>
                    <p className='text-sm text-gray-500'>Top Likers</p>
                    <p className='mt-1 text-3xl font-bold text-blue-600'>{e?.topLikers?.length ?? 0}</p>
                  </div>
                </div>

                {(e?.topSenders?.length ?? 0) > 0 && (
                  <div>
                    <p className='mb-2 text-sm font-semibold text-gray-700'>Top Senders</p>
                    <div className='overflow-x-auto rounded-lg border border-gray-200'>
                      <table className='min-w-full divide-y divide-gray-100'>
                        <thead className='bg-gray-50'>
                          <tr>
                            <th className='px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>User</th>
                            <th className='px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>User ID</th>
                            <th className='px-4 py-2 text-right text-xs font-semibold uppercase tracking-wider text-gray-500'>Total Sent</th>
                          </tr>
                        </thead>
                        <tbody className='divide-y divide-gray-100 bg-white'>
                          {e!.topSenders.map((u, i) => (
                            <tr key={u.userId} className='hover:bg-orange-50/40'>
                              <td className='px-4 py-3 text-sm'>
                                <div className='flex items-center gap-2'>
                                  <span className='w-5 text-xs font-bold text-gray-400'>#{i + 1}</span>
                                  {u.profileImage && (
                                    <img src={u.profileImage} alt='' className='h-7 w-7 rounded-full object-cover' />
                                  )}
                                  <span className='font-medium text-gray-900'>
                                    {u.name || (u.username ? `@${u.username}` : u.userId)}
                                  </span>
                                </div>
                              </td>
                              <td className='px-4 py-3 font-mono text-xs text-gray-400'>{u.userId}</td>
                              <td className='px-4 py-3 text-right text-sm font-bold text-amber-600'>{u.totalSent ?? '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {(e?.topLikers?.length ?? 0) > 0 && (
                  <div>
                    <p className='mb-2 text-sm font-semibold text-gray-700'>Top Likers</p>
                    <div className='overflow-x-auto rounded-lg border border-gray-200'>
                      <table className='min-w-full divide-y divide-gray-100'>
                        <thead className='bg-gray-50'>
                          <tr>
                            <th className='px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>User</th>
                            <th className='px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>User ID</th>
                            <th className='px-4 py-2 text-right text-xs font-semibold uppercase tracking-wider text-gray-500'>Likes</th>
                          </tr>
                        </thead>
                        <tbody className='divide-y divide-gray-100 bg-white'>
                          {e!.topLikers.map((u, i) => (
                            <tr key={u.userId} className='hover:bg-orange-50/40'>
                              <td className='px-4 py-3 text-sm'>
                                <div className='flex items-center gap-2'>
                                  <span className='w-5 text-xs font-bold text-gray-400'>#{i + 1}</span>
                                  {u.profileImage && (
                                    <img src={u.profileImage} alt='' className='h-7 w-7 rounded-full object-cover' />
                                  )}
                                  <span className='font-medium text-gray-900'>
                                    {u.name || (u.username ? `@${u.username}` : u.userId)}
                                  </span>
                                </div>
                              </td>
                              <td className='px-4 py-3 font-mono text-xs text-gray-400'>{u.userId}</td>
                              <td className='px-4 py-3 text-right text-sm font-bold text-rose-500'>{u.totalLikes ?? '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Notifications */}
            <div className='rounded-xl border border-gray-200 bg-white shadow-sm'>
              <div className='border-b border-gray-200 px-5 py-4'>
                <h2 className='text-base font-semibold text-gray-900'>Go-Live Notifications</h2>
                <p className='text-sm text-gray-500'>
                  Push notification fanout sent to followers when this session went live.
                </p>
              </div>
              <div className='p-5'>
                {!n ? (
                  <p className='text-sm text-gray-400 italic'>No notification data recorded for this session.</p>
                ) : n.skipped ? (
                  <div className='flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4'>
                    <span className='mt-0.5 text-lg text-amber-500'>⚠</span>
                    <div>
                      <p className='text-sm font-semibold text-amber-800'>Notifications skipped</p>
                      <p className='mt-0.5 text-xs text-amber-700'>{n.skippedReason || 'No reason recorded'}</p>
                    </div>
                  </div>
                ) : (
                  <div className='space-y-4'>
                    <div className='flex items-center gap-3'>
                      <span className='inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-600'>
                        ✓
                      </span>
                      <div>
                        <p className='text-sm font-semibold text-gray-900'>Notifications sent</p>
                        {n.sentAt && <p className='text-xs text-gray-400'>{formatDate(n.sentAt)}</p>}
                      </div>
                    </div>
                    <div className='grid grid-cols-2 gap-3 sm:grid-cols-4'>
                      <StatCell label='Total Sent' value={n.totalSent ?? '—'} tone='text-emerald-600' />
                      <StatCell label='Devices Reached' value={n.devicesReached ?? '—'} />
                      <StatCell label='iOS Sent' value={n.iosSent ?? '—'} />
                      <StatCell label='Android Sent' value={n.androidSent ?? '—'} />
                      <StatCell label='Eligible Followers' value={n.eligibleFollowers ?? '—'} />
                      <StatCell label='Total Followers' value={n.totalFollowers ?? '—'} />
                      <StatCell
                        label='Failed'
                        value={n.failed ?? '—'}
                        tone={(n.failed ?? 0) > 0 ? 'text-rose-600' : 'text-gray-900'}
                      />
                      <StatCell
                        label='Coverage'
                        value={
                          n.totalFollowers && n.devicesReached
                            ? `${Math.round((n.devicesReached / n.totalFollowers) * 100)}%`
                            : '—'
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Polls */}
            <div className='rounded-xl border border-gray-200 bg-white shadow-sm'>
              <div className='flex items-center justify-between border-b border-gray-200 px-5 py-4'>
                <div>
                  <h2 className='text-base font-semibold text-gray-900'>Polls</h2>
                  <p className='text-sm text-gray-500'>Polls created during this session.</p>
                </div>
                <span className='inline-flex rounded-full border border-gray-200 bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-700'>
                  {p?.total ?? 0}
                </span>
              </div>
              <div className='p-5'>
                {!p?.total ? (
                  <p className='text-sm text-gray-400 italic'>No polls were created during this session.</p>
                ) : (
                  <div className='space-y-4'>
                    {p.items.map((poll, i) => {
                      const totalVotes = poll.options.reduce((sum, o) => sum + o.votes, 0);
                      return (
                        <div key={poll.pollId || i} className='rounded-lg border border-gray-200 bg-gray-50 p-4'>
                          <div className='flex items-start justify-between gap-3'>
                            <p className='text-sm font-semibold text-gray-900'>{poll.question}</p>
                            <span className={`flex-shrink-0 inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
                              poll.status === 'ACTIVE'
                                ? 'border-emerald-200 bg-emerald-100 text-emerald-700'
                                : 'border-gray-200 bg-gray-100 text-gray-600'
                            }`}>{poll.status}</span>
                          </div>
                          <div className='mt-1 flex flex-wrap items-center gap-3 text-xs text-gray-400'>
                            {poll.durationSeconds && <span>{formatDuration(poll.durationSeconds)}</span>}
                            {poll.startedAt && <span>Started {formatDate(poll.startedAt)}</span>}
                            {poll.closedAt && <span>Closed {formatDate(poll.closedAt)}</span>}
                            <span>{totalVotes} vote{totalVotes !== 1 ? 's' : ''}</span>
                          </div>
                          <div className='mt-3 space-y-2'>
                            {poll.options.map((opt, j) => {
                              const pct = totalVotes > 0 ? Math.round((opt.votes / totalVotes) * 100) : 0;
                              return (
                                <div key={j}>
                                  <div className='mb-1 flex items-center justify-between text-xs'>
                                    <span className='text-gray-700'>{opt.text}</span>
                                    <span className='font-semibold text-gray-900'>{opt.votes} ({pct}%)</span>
                                  </div>
                                  <div className='h-2 w-full rounded-full bg-gray-200'>
                                    <div
                                      className='h-2 rounded-full bg-gradient-to-r from-[#FF9C15] to-[#F42026] transition-all'
                                      style={{ width: `${pct}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Moderation */}
            <div className='rounded-xl border border-gray-200 bg-white shadow-sm'>
              <div className='flex items-center justify-between border-b border-gray-200 px-5 py-4'>
                <div>
                  <h2 className='text-base font-semibold text-gray-900'>Moderation & Reports</h2>
                  <p className='text-sm text-gray-500'>User reports filed against this session.</p>
                </div>
                <span className={`inline-flex rounded-full border px-3 py-1 text-sm font-semibold ${
                  (m?.reportCount ?? 0) > 0
                    ? 'border-rose-200 bg-rose-100 text-rose-700'
                    : 'border-gray-200 bg-gray-100 text-gray-700'
                }`}>{m?.reportCount ?? 0} reports</span>
              </div>
              <div className='p-5'>
                {!m?.reportCount ? (
                  <p className='text-sm text-gray-400 italic'>No reports for this session.</p>
                ) : (
                  <div className='overflow-x-auto rounded-lg border border-gray-200'>
                    <table className='min-w-full divide-y divide-gray-100'>
                      <thead className='bg-gray-50'>
                        <tr>
                          <th className='px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Reporter</th>
                          <th className='px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Reason</th>
                          <th className='px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Details</th>
                          <th className='px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Reported At</th>
                        </tr>
                      </thead>
                      <tbody className='divide-y divide-gray-100 bg-white'>
                        {m!.reports.map((r, i) => (
                          <tr key={r.reportId || i} className='hover:bg-rose-50/30'>
                            <td className='px-4 py-3 text-sm'>
                              <p className='font-medium text-gray-900'>
                                {r.reporterUserName ? `@${r.reporterUserName}` : (r.reporterUserId || '—')}
                              </p>
                              {r.reporterUserId && (
                                <p className='font-mono text-xs text-gray-400'>{r.reporterUserId}</p>
                              )}
                            </td>
                            <td className='px-4 py-3 text-sm'>
                              <span className='inline-flex rounded-full border border-rose-200 bg-rose-50 px-2.5 py-0.5 text-xs font-semibold text-rose-700'>
                                {r.reasonLabel || r.reasonCode || '—'}
                              </span>
                            </td>
                            <td className='max-w-xs px-4 py-3 text-sm text-gray-600'>
                              <span className='line-clamp-2'>{r.details || '—'}</span>
                            </td>
                            <td className='px-4 py-3 text-xs text-gray-500'>{formatDate(r.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LivestreamAnalyticsDetail;
